/**
 * Player API — Provably fair.
 *
 * The ticket endpoint runs in the player's browser with the session token,
 * like the rest of the Player API. The key and receipt endpoints are public:
 * anyone holding a receipt id can check it, no credential needed. The hosted
 * player iframe calls all three itself; these are documented for partners who
 * build their own UI and for anyone verifying a receipt independently.
 */
import { SECURITY } from './security';

const EXAMPLE_TICKET = {
  id: '5f0c2a9e7d3b41c8a6e2f19b0d4c7a35',
  proto: 'pf-a-v1',
  pool_offer_id: 463,
  server_seed_hash: '17a7384bf1c50a94b712ce507c5ccd58638cd091278078c6e7dc00ae0aa152fc',
  odds: {
    normal: [
      { tier: '0..50', ticks: 78500000 },
      { tier: '50..100', ticks: 18000000 },
      { tier: '100..Infinity', ticks: 3500000 },
    ],
    high: [
      { tier: '0..50', ticks: 82000000 },
      { tier: '50..100', ticks: 12500000 },
      { tier: '100..Infinity', ticks: 5500000 },
    ],
    max: [
      { tier: '0..50', ticks: 86000000 },
      { tier: '50..100', ticks: 6500000 },
      { tier: '100..Infinity', ticks: 7500000 },
    ],
  },
  odds_digest: '9a3f…',
  params_fingerprint: '576e3d9124ef023f41e9766aed9a36803d1e4eb5b4148e09bb9d0909cdfedbe2',
  max_quantity: 3,
  issued_at_ms: 1760000000000,
  valid_until_ms: 1760000090000,
  signing_key_id: '036d2c500c35c97a',
  signature: 'e13d779c…5c46',
};

const ERROR = { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } };

export const fairnessPaths: Record<string, unknown> = {
  '/loot_machine/api/game_platform/iframe/v1/pool_offers/{pool_offer_id}/fairness_tickets': {
    post: {
      tags: ['Provably Fair'],
      summary: 'Get a ticket',
      description: [
        'The signed commitment for the **next** purchase of this game: the SHA-256 hash of a fresh server seed and',
        'the odds of every volatility level, fixed before the player seed exists.',
        '',
        'Request one while the player looks at the pack, show `server_seed_hash`, generate a fresh **player seed**',
        '(32 random bytes as 64 hex characters, e.g. `crypto.getRandomValues`), and send both with the purchase:',
        '`fairness: { ticket_id, client_seed }` on **Buy a pack (one call)**.',
        '',
        '- One ticket buys **once**, any volatility level and any quantity up to `max_quantity`.',
        '- It expires about **90 s** after issue (`valid_until_ms`); request a new one shortly before.',
        '- A player holds at most 5 usable tickets per game — a newer one retires the oldest — and may request 30',
        '  tickets per minute.',
        '- Use a **new** player seed for every purchase; a seed reused within the session is rejected.',
        '',
        'Available only while provably fair is on for your platform (we switch it on — see *Provably fair* in the',
        'overview); the player iframe reads it from `provably_fair: true` in **Get the resolved theme**.',
      ].join('\n'),
      operationId: 'issueFairnessTicket',
      security: SECURITY.player,
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'The game (pack) the next purchase is for.',
        },
      ],
      responses: {
        '200': {
          description: 'A fresh ticket.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['ticket'],
                    properties: { ticket: { $ref: '#/components/schemas/FairnessTicket' } },
                  },
                },
              },
              example: { result: { ticket: EXAMPLE_TICKET } },
            },
          },
        },
        '401': { description: 'Missing or invalid player session token.', content: ERROR },
        '404': { description: 'Game not found or not available to your platform.', content: ERROR },
        '422': {
          description: [
            '`FAIRNESS_DISABLED` — provably fair is off for your platform.',
            '`FAIRNESS_NOT_APPLICABLE` — the player is on your promotion list; their purchases are never provably fair.',
            '`FAIRNESS_UNAVAILABLE` — the odds of this pack cannot be fixed right now; buy without a ticket or retry later.',
          ].join(' '),
          content: ERROR,
        },
        '429': { description: '`FAIRNESS_RATE_LIMITED` — more than 30 tickets in a minute for this session.', content: ERROR },
      },
    },
  },

  '/loot_machine/api/game_platform/fairness/keys': {
    get: {
      tags: ['Provably Fair'],
      summary: 'Get the signing keys',
      description: [
        'The public keys Repackz signs tickets with. Match a ticket’s `signing_key_id` to `key_id` and verify its',
        '`signature` with that key. **Public:** no credential.',
      ].join('\n'),
      operationId: 'getFairnessKeys',
      security: SECURITY.none,
      responses: {
        '200': {
          description: 'Current signing keys.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  result: {
                    type: 'object',
                    properties: {
                      keys: {
                        type: 'array',
                        items: {
                          type: 'object',
                          required: ['key_id', 'algorithm', 'public_key_pem'],
                          properties: {
                            key_id: { type: 'string', description: 'First 16 hex characters of SHA-256 over the key’s SubjectPublicKeyInfo DER.' },
                            algorithm: { type: 'string', enum: ['ECDSA_P256_SHA256'] },
                            public_key_pem: { type: 'string', description: 'The public key, PEM (SubjectPublicKeyInfo).' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  keys: [
                    {
                      key_id: '036d2c500c35c97a',
                      algorithm: 'ECDSA_P256_SHA256',
                      public_key_pem: '-----BEGIN PUBLIC KEY-----\nMFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE…\n-----END PUBLIC KEY-----\n',
                    },
                  ],
                },
              },
            },
          },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/fairness/receipts/{ticket_id}': {
    get: {
      tags: ['Provably Fair'],
      summary: 'Get a receipt',
      description: [
        'A ticket’s receipt: the signed ticket, both seeds and the roll of every card slot. **Public:** no',
        'credential — the 128-bit ticket id is the only key, and the receipt names no player. The public checker',
        '`https://verify.repackz.fun/?ticket=<ticket id>` loads it from here.',
        '',
        '`server_seed` stays null while the purchase can still change (`state: consumed`) and appears once it is',
        'over (`state: dead`) — normally in the purchase response itself, at the latest within a few minutes. A ticket never used',
        'reveals its seed once it expires, and is kept for ten minutes after that.',
      ].join('\n'),
      operationId: 'getFairnessReceipt',
      security: SECURITY.none,
      parameters: [
        {
          name: 'ticket_id',
          in: 'path',
          required: true,
          schema: { type: 'string', pattern: '^[0-9a-f]{32}$' },
          description: 'The ticket id (`ticket.id`, 32 hex characters).',
        },
      ],
      responses: {
        '200': {
          description: 'The receipt.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  result: {
                    type: 'object',
                    properties: { receipt: { $ref: '#/components/schemas/FairnessReceipt' } },
                  },
                },
              },
              example: {
                result: {
                  receipt: {
                    proto: 'pf-a-v1',
                    state: 'dead',
                    ticket: EXAMPLE_TICKET,
                    client_seed: '3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c',
                    volatility: 'normal',
                    quantity: 1,
                    server_seed: '7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f7f',
                    slots: [{ slot: 0, roll: 31068478, tier: '0..50', provably_fair: true }],
                    pack: { id: 463, title: 'HOF ELITE MMA Pack' },
                  },
                },
              },
            },
          },
        },
        '404': { description: 'No receipt with this id (unknown, malformed, or an unused ticket past its retention).', content: ERROR },
      },
    },
  },
};
